import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	ViewEncapsulation,
	forwardRef,
	ViewChild,
	ElementRef,
	OnDestroy,
	SimpleChanges,
	OnChanges,
	HostBinding,
	AfterViewInit
} from "@angular/core";
import { NG_VALUE_ACCESSOR, NgModel, ControlValueAccessor } from "@angular/forms";
import { IdService } from "../shared/services/id.service";


@Component({
	selector: "rbc-uploader",
	templateUrl: "./uploader.component.html",
	styles: [],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => UploaderComponent),
			multi: true
		}
	]
})
export class UploaderComponent implements ControlValueAccessor, OnDestroy, OnInit {
	// Private Members
	defaultError = "Invalid";
	focused = false;
	statusMessage = null;
	typeOverwrite;
	// Allow the input to be disabled, and when it is make it somewhat transparent.
	@Input() id: string;
	@Input() accept: string;
	@Input() disabled = false;
	@Input() fileChooseLabel = "Choose a file";
	@Input() fileNoneChosenLabel = "No file chosen";
	@Input() helpText: string;
	@Input() helpTextSrOnly;
	@Input() label: string;
	@Input() labelSrOnly;
	@Input() size: "small" | "medium" | "large" = "medium";
	@Input() status: boolean | string;
	@Input() statusType: "danger" | "success" | "warning" | "info";
	@Input() multiple = false;
	@Input() showFileList = false;
	@Input() removeLabel = "Remove";
	@Input() retryLabel = "Retry";
	@Input() filesSelectedLabel = "selected";
	@Input() uploadFailLabel = "Upload Failed";

	// Aria a11y attributes
	@Input() required= false;
	@Input() ariaLabel: string;
	@Input() ariaDescribedBy: string;
	@Input() ariaLabelledBy: string;


	_value: File | [] | Object;
	get value() {
		return this._value;
	}
	@Input() set value(value: File | [] | Object) {
		this._value = value;
		this.files = this._value;
		if(!value){
			this.uploaderInput.nativeElement.value = null;
		}
	}

	@Output() onInput = new EventEmitter<string>();
	@Output() fileRemoved = new EventEmitter<any>();
	@Output() fileRetry = new EventEmitter<any>();

	@ViewChild("uploaderInput", { static: true }) uploaderInput;

	files = null;

	get fileList() {
		if (this.files && this.multiple) {
			return this.files.map(({ file, isError, isUploading, progress }) => {
				return {
					name: file.name,
					size: Math.round(parseInt(file.size) / 1000),
					isError,
					isUploading,
					progress
				};
			});
		} else {
			return null;
		}
	}

	constructor(private idService: IdService) {
		this.id = this.id ? this.id : this.idService.generate();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	ngOnInit() {
		this.fileDisplayText();
	}

	// Function to call when the rating changes.
	onChange = (files) => {};
	// Function to call when the input is touched (when a star is clicked).
	onTouched = () => { };
	get inputValue() {
		return this.value;
	}

	// Allows Angular to update the model (rating).
	// Update the model and changes needed for the view here.
	writeValue(rating): void {
		this.value = rating;
		this.onChange(this.inputValue);
	}
	// Allows Angular to register a function to call when the model (rating) changes.
	// Save the function as a property to call later here.
	registerOnChange(fn: (rating: number) => void): void {
		this.onChange = fn;
	}
	// Allows Angular to register a function to call when the input has been touched.
	// Save the function as a property to call later here.
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	// Allows Angular to disable the input.
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	setInputValue({ target }) {
		this.files = target.files && target.files.length ?
			(this.multiple ? [...target.files].map(file => {
				return {
					file,
					isUploading: false,
					isError: false,
					progress: 0
				};
			}) :
			target.files[0]) : null;
		this.writeValue(this.files);
		this.onInput.emit(this.files);
	}

	removeFile(index) {
		this.fileRemoved.emit({ file: this.files[index], index });
		this.files.splice(index, 1);
		this.writeValue(this.files);
	}

	retryFile(index) {
		this.fileRetry.emit({ file: this.files[index], index });
		this.files[index].isUploading = true;
		this.files[index].isError = false;
		this.files[index].progress = 0;
	}

	fileDisplayText() {
		if (this.files && (!this.multiple || (this.multiple && this.files.length))) {
			if (this.multiple) {
				return (this.files.length > 1) ?
					`${this.files.length} ${this.filesSelectedLabel}` :
					this.files[0].name;

			} else {
				return this.files.name;
			}
		} else {
			return this.fileNoneChosenLabel;
		}
	}

	describedbyGen() {
		if (this.status || this.helpText || this.ariaDescribedBy) {
			const descriptors = {
				[`${this.id}Status`]: this.status,
				[`${this.id}HelpText`]: this.helpText,
				[this.ariaDescribedBy]: this.ariaDescribedBy
			};
			return Object.keys(descriptors).filter(key => descriptors[key]).join(" ");

		} else {
			return null;
		}
	}
}
